import { useMemo, useState } from "react";
import { clsx } from "clsx";
import { ChevronDown, Paperclip } from "lucide-react";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { DatePicker } from "@/components/ui/DatePicker";
import { FileDropZone } from "@/components/ui/FileDropZone";
import { Modal } from "@/components/ui/Modal";
import { MultiSelect } from "@/components/ui/MultiSelect";
import { Spinner } from "@/components/ui/Spinner";
import { EmptyState, SubTabs } from "@/features/power_projects/components/ui";
import * as api from "../api";
import { useInstitutions, useLedMutation, useRegister } from "../hooks";
import {
  REGISTER_KIND_LABEL,
  type LedParcel,
  type RegisterDirection,
  type RegisterInput,
  type RegisterKind,
  type RegisterRow,
} from "../types";
import { DEFAULT_RESPONSE_DAYS, ErrorLine, Field, FileChips, INPUT, OnlyUs, addDays, fmtDate, today } from "./common";

const DIRECTION_LABEL: Record<RegisterDirection, string> = {
  out: "wychodzące",
  in: "przychodzące",
};
const DIRECTION_INK: Record<RegisterDirection, string> = {
  out: "var(--color-primary)",
  in: "#1d4ed8",
};

/** Jedna plakietka mówiąca, na czym sprawa stoi — zamiast kolumny „termin" z myślnikiem. */
function statusOf(r: RegisterRow): { color: "green" | "yellow" | "red" | "blue" | "gray"; label: string } | null {
  if (r.direction === "in") return { color: "blue", label: `wpłynęło ${fmtDate(r.entry_date)}` };
  if (r.answered_on) return { color: "green", label: `odpowiedź ${fmtDate(r.answered_on)}` };
  if (r.overdue) return { color: "red", label: `bez odpowiedzi od ${fmtDate(r.response_due)}` };
  if (r.response_due) return { color: "yellow", label: `czeka na odpowiedź do ${fmtDate(r.response_due)}` };
  return { color: "gray", label: "wysłane" };
}

const KINDS_BY_DIRECTION: Record<RegisterDirection, RegisterKind[]> = {
  out: ["letter_to_office", "letter_to_owner", "reminder"],
  in: ["response_office", "response_owner"],
};
type Filter = "all" | "out" | "in" | "unread" | "overdue";

interface Props {
  leadershipId: string;
  internal: boolean;
  parcels: LedParcel[];
  onOpenParcel: (parcelId: string) => void;
}

function LetterDialog({ leadershipId, parcels, rows, open, onClose }: { leadershipId: string; parcels: LedParcel[]; rows: RegisterRow[]; open: boolean; onClose: () => void }) {
  const { data: institutions } = useInstitutions(leadershipId, open);
  const [direction, setDirection] = useState<RegisterDirection>("out");
  const [kind, setKind] = useState<RegisterKind>("letter_to_office");
  const blank = (): RegisterInput => ({
    direction: "out",
    kind: "letter_to_office",
    entry_date: today(),
    // Termin odpowiedzi podpowiadamy z góry (14 dni) — i tak najczęściej tyle daje się
    // urzędowi; data zostaje do zmiany jednym kliknięciem.
    response_due: addDays(today(), DEFAULT_RESPONSE_DAYS),
    parcels: [],
  });
  const [f, setF] = useState<RegisterInput>(blank);
  /** Gdy termin nie był ruszany ręcznie, jedzie za datą pisma. */
  const [dueTouched, setDueTouched] = useState(false);
  const [files, setFiles] = useState<File[]>([]);
  const add = useLedMutation(leadershipId, () => api.addRegister(leadershipId, { ...f, direction, kind, files }), ["register"]);
  const owners = useMemo(() => parcels.flatMap((p) => p.owners.map((o) => ({ id: o.id, label: `${o.full_name} — ${p.teryt}` }))), [parcels]);
  const toOffice = kind === "letter_to_office" || kind === "response_office" || kind === "reminder";
  const set = (patch: Partial<RegisterInput>) => setF((x) => ({ ...x, ...patch }));
  const reset = () => { setF(blank()); setFiles([]); setDirection("out"); setKind("letter_to_office"); setDueTouched(false); };
  const valid = !!f.entry_date && (toOffice ? !!(f.institution || f.counterparty?.trim()) : !!f.owner);

  return (
    <Modal open={open} onClose={onClose} title="Nowe pismo w rejestrze spraw" size="lg">
      <div className="space-y-4 p-1 text-[13px]">
        <div className="flex flex-wrap items-center gap-2">
          {(["out", "in"] as RegisterDirection[]).map((d) => (
            <button
              key={d}
              type="button"
              className={clsx(
                "border px-3 py-1 font-mono text-[11px] uppercase tracking-wider",
                direction === d
                  ? "border-[var(--color-primary)] bg-[var(--color-primary)] text-white"
                  : "border-[var(--color-border)] text-[var(--color-text-secondary)] hover:border-[var(--color-primary)]",
              )}
              onClick={() => {
                setDirection(d);
                setKind(KINDS_BY_DIRECTION[d][0]);
                set({ response_due: d === "out" ? addDays(f.entry_date, DEFAULT_RESPONSE_DAYS) : null });
              }}
            >
              {d === "out" ? "pismo wychodzące" : "pismo przychodzące"}
            </button>
          ))}
        </div>

        <Group title={direction === "out" ? "Co wysyłamy" : "Co wpłynęło"}>
          <Field label="Rodzaj" className="sm:col-span-2">
            <select className={INPUT} value={kind} onChange={(e) => setKind(e.target.value as RegisterKind)}>
              {KINDS_BY_DIRECTION[direction].map((k) => (
                <option key={k} value={k}>{REGISTER_KIND_LABEL[k]}</option>
              ))}
            </select>
          </Field>
          <Field label={direction === "out" ? "Data wysłania" : "Data wpływu"}>
            <DatePicker
              value={f.entry_date}
              clearable={false}
              onChange={(v) => {
                const d = v || today();
                set({
                  entry_date: d,
                  ...(direction === "out" && !dueTouched
                    ? { response_due: addDays(d, DEFAULT_RESPONSE_DAYS) }
                    : {}),
                });
              }}
            />
          </Field>
        </Group>

        <Group title={direction === "out" ? "Do kogo" : "Od kogo"}>
          {toOffice ? (
            <>
              <Field label="Urząd / instytucja z katalogu projektu" className="sm:col-span-2">
                <select
                  className={INPUT}
                  value={f.institution ?? ""}
                  onChange={(e) => set({ institution: e.target.value || null })}
                >
                  <option value="">— spoza katalogu, wpiszę ręcznie —</option>
                  {(institutions ?? []).map((i) => (
                    <option key={i.id} value={i.id}>{i.name}</option>
                  ))}
                </select>
              </Field>
              <Field label="Nazwa spoza katalogu">
                <input
                  className={INPUT}
                  value={f.counterparty ?? ""}
                  disabled={!!f.institution}
                  placeholder={f.institution ? "— z katalogu —" : "np. PGE Dystrybucja"}
                  onChange={(e) => set({ counterparty: e.target.value })}
                />
              </Field>
            </>
          ) : (
            <>
              <Field label="Właściciel / strona" className="sm:col-span-2">
                <select className={INPUT} value={f.owner ?? ""} onChange={(e) => set({ owner: e.target.value || null })}>
                  <option value="">— wybierz —</option>
                  {owners.map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
                </select>
              </Field>
              <Field label="Adresat inny niż strona">
                <input
                  className={INPUT}
                  value={f.counterparty ?? ""}
                  placeholder="np. pełnomocnik"
                  onChange={(e) => set({ counterparty: e.target.value })}
                />
              </Field>
            </>
          )}
        </Group>

        <Group title="Terminy i oznaczenia">
          <Field label="Znak pisma">
            <input className={INPUT} value={f.reference ?? ""} onChange={(e) => set({ reference: e.target.value })} />
          </Field>
          <Field label="Nr przesyłki">
            <input
              className={INPUT}
              value={f.tracking_number ?? ""}
              onChange={(e) => set({ tracking_number: e.target.value })}
            />
          </Field>
          {direction === "out" ? (
            <>
              <Field label="Termin odpowiedzi" hint={`domyślnie ${DEFAULT_RESPONSE_DAYS} dni od daty pisma`}>
                <DatePicker
                  value={f.response_due ?? ""}
                  min={f.entry_date}
                  onChange={(v) => { setDueTouched(true); set({ response_due: v || null }); }}
                />
              </Field>
              <Field label="Doręczono">
                <DatePicker value={f.delivered_on ?? ""} min={f.entry_date} onChange={(v) => set({ delivered_on: v || null })} />
              </Field>
            </>
          ) : (
            <Field label="Odpowiedź na nasze pismo" className="sm:col-span-2">
              <select className={INPUT} value={f.responds_to ?? ""} onChange={(e) => set({ responds_to: e.target.value || null })}>
                <option value="">— samodzielne pismo —</option>
                {rows.filter((r) => r.direction === "out").map((r) => (
                  <option key={r.id} value={r.id}>{r.label} · {r.counterparty} · {fmtDate(r.entry_date)}</option>
                ))}
              </select>
            </Field>
          )}
        </Group>

        <Group title="Czego dotyczy" cols={1}>
          <Field label="Działki (szukaj po numerze)">
            <MultiSelect
              options={parcels.map((p) => ({
                value: p.id,
                label: `${p.teryt}${p.in_current_route ? "" : " (poza trasą)"}`,
              }))}
              value={f.parcels ?? []}
              onChange={(v) => set({ parcels: v })}
              placeholder="— wskaż działki —"
              searchPlaceholder="Szukaj numeru działki..."
              chipsMaxHeight={96}
            />
          </Field>
          <Field label="Treść / streszczenie">
            <textarea className={clsx(INPUT, "min-h-[4.5rem]")} value={f.body ?? ""} onChange={(e) => set({ body: e.target.value })} />
          </Field>
          <FileDropZone files={files} onFilesChange={setFiles} multiple label="Skan pisma / załączniki" />
        </Group>

        <ErrorLine error={add.error} />
        <div className="flex items-center justify-end gap-2 border-t border-[var(--color-border)] pt-3">
          {!valid && (
            <span className="mr-auto text-[11px] text-[var(--color-text-muted)]">
              {toOffice ? "Wskaż urząd z katalogu albo wpisz nazwę adresata." : "Wskaż właściciela, którego dotyczy pismo."}
            </span>
          )}
          <Button variant="ghost" onClick={onClose}>Anuluj</Button>
          <Button disabled={!valid || add.isPending} onClick={async () => { await add.mutateAsync(); reset(); onClose(); }}>
            Zapisz w rejestrze
          </Button>
        </div>
      </div>
    </Modal>
  );
}

/** Sekcja formularza — nagłówek i siatka pól; dialog czyta się wtedy z góry na dół,
 *  a nie jak tabela z przypadkowo poukładanymi polami. */
function Group({ title, cols = 3, children }: { title: string; cols?: 1 | 3; children: React.ReactNode }) {
  return (
    <section>
      <h4 className="section-label mb-1.5 border-b border-[var(--color-border-muted)] pb-1">{title}</h4>
      <div className={clsx("grid gap-3", cols === 1 ? "grid-cols-1" : "grid-cols-1 sm:grid-cols-3")}>{children}</div>
    </section>
  );
}

/** Rejestr spraw — pisma wychodzące i przychodzące; dodają obie strony. Praca przy
 *  konkretnym właścicielu (stan tytułu, problem, notatka, plik) siedzi na karcie działki. */
export function RegisterTab({ leadershipId, internal, parcels, onOpenParcel }: Props) {
  const { data, isLoading, error } = useRegister(leadershipId);
  const [filter, setFilter] = useState<Filter>("all");
  const [open, setOpen] = useState<string | null>(null);
  const [letterOpen, setLetterOpen] = useState(false);
  const read = useLedMutation(leadershipId, (id: string) => api.registerAction(leadershipId, id, "read"), ["register"]);
  const share = useLedMutation(leadershipId, (a: { id: string; shared: boolean }) => api.registerAction(leadershipId, a.id, "share", { shared: a.shared }), ["register"]);
  const rows = data ?? [];
  const visible = rows.filter((r) => (filter === "all" ? true : filter === "unread" ? r.unread : filter === "overdue" ? r.overdue : r.direction === filter));
  const counts = { unread: rows.filter((r) => r.unread).length, overdue: rows.filter((r) => r.overdue).length };

  if (isLoading) return <div className="flex h-40 items-center justify-center"><Spinner /></div>;
  if (error) return <ErrorLine error={error} />;
  return (
    <div>
      <div className="mb-2 flex flex-wrap items-start justify-between gap-2">
        <SubTabs<Filter>
          active={filter}
          onChange={setFilter}
          tabs={[
            { key: "all", label: "Wszystkie", count: rows.length || undefined, countTone: "blue" },
            { key: "out", label: "Wychodzące" },
            { key: "in", label: "Przychodzące" },
            { key: "unread", label: "Nieprzeczytane", count: counts.unread || undefined, countTone: "warn" },
            { key: "overdue", label: "Po terminie", count: counts.overdue || undefined, countTone: "danger" },
          ]}
        />
        <Button size="sm" onClick={() => setLetterOpen(true)}>+ pismo</Button>
      </div>
      {rows.length === 0 ? (
        <div className="card"><EmptyState>Rejestr jest pusty — dodaj pierwsze pismo</EmptyState></div>
      ) : (
        <ul className="space-y-1.5">
          {visible.map((r) => {
            const expanded = open === r.id;
            const status = statusOf(r);
            return (
              <li
                key={r.id}
                className={clsx("card", r.unread && "bg-amber-50/70 dark:bg-amber-950/25")}
                style={{ borderLeft: `3px solid ${DIRECTION_INK[r.direction]}` }}
              >
                <button
                  type="button"
                  className="flex w-full flex-wrap items-baseline gap-x-2 gap-y-1 px-3 py-2 text-left hover:bg-[var(--color-bg-elevated)]"
                  onClick={() => { setOpen(expanded ? null : r.id); if (r.unread && !expanded) read.mutate(r.id); }}
                >
                  <span className="font-mono text-[11px] font-bold text-[var(--color-text-muted)]">{r.label}</span>
                  <span
                    className="whitespace-nowrap border px-1.5 py-px font-mono text-[10px] uppercase tracking-wider"
                    style={{ borderColor: DIRECTION_INK[r.direction], color: DIRECTION_INK[r.direction] }}
                  >
                    {DIRECTION_LABEL[r.direction]}
                  </span>
                  <span className="text-[14px] font-bold leading-tight">{r.counterparty || "—"}</span>
                  <span className="text-[13px] text-[var(--color-text-secondary)]">{r.kind_display}</span>
                  {r.unread && <span className="h-2 w-2 bg-[var(--color-primary)]" title="nieprzeczytane" />}
                  <span className="ml-auto flex items-center gap-2">
                    {status && <Badge color={status.color}>{status.label}</Badge>}
                    <ChevronDown
                      size={14}
                      className={clsx("text-[var(--color-text-muted)] transition-transform", expanded && "rotate-180")}
                    />
                  </span>
                </button>
                <div className="flex flex-wrap items-center gap-x-2 gap-y-1 px-3 pb-2 text-[11px] text-[var(--color-text-muted)]">
                  <span className="font-mono">{fmtDate(r.entry_date)}</span>
                  {r.reference && <span>· znak <span className="font-mono">{r.reference}</span></span>}
                  {r.responds_to_label && <span>· odpowiedź na {r.responds_to_label}</span>}
                  <span>· {r.author_label}</span>
                  {r.files.length > 0 && (
                    <span className="inline-flex items-center gap-1">· <Paperclip size={11} /> {r.files.length}</span>
                  )}
                  {r.prehandover && <span>· sprzed przekazania</span>}
                  {r.parcels.length > 0 && (
                    <span className="flex flex-wrap items-center gap-1">
                      ·
                      {r.parcels.slice(0, 3).map((p) => (
                        <button
                          key={p.id}
                          type="button"
                          className="border border-[var(--color-border)] px-1.5 py-px font-mono text-[10px] hover:border-[var(--color-primary)] hover:text-[var(--color-primary)]"
                          onClick={() => onOpenParcel(p.id)}
                        >
                          {p.teryt}
                        </button>
                      ))}
                      {r.parcels.length > 3 && <span>+{r.parcels.length - 3}</span>}
                    </span>
                  )}
                </div>
                {expanded && (
                  <div className="border-t border-[var(--color-border-muted)] px-3 py-2.5 text-[13px]">
                    {r.tracking_number && (
                      <p className="text-[11px] text-[var(--color-text-muted)]">
                        przesyłka <span className="font-mono">{r.tracking_number}</span>
                        {r.delivered_on && <> · doręczono {fmtDate(r.delivered_on)}</>}
                      </p>
                    )}
                    {r.body && <p className="mt-1 whitespace-pre-wrap leading-relaxed">{r.body}</p>}
                    <FileChips files={r.files} className="mt-2" />
                    {r.unread && (
                      <Button size="sm" className="mt-2" onClick={() => read.mutate(r.id)}>Oznacz jako przeczytane</Button>
                    )}
                    {internal && r.prehandover && (
                      <OnlyUs className="mt-3">
                        Pismo z naszej korespondencji sprzed przekazania — podwykonawca widzi je domyślnie.{" "}
                        <label className="ml-1 inline-flex items-center gap-1.5">
                          <input
                            type="checkbox"
                            checked={!r.hidden_from_contractor}
                            disabled={share.isPending}
                            onChange={(e) => share.mutate({ id: r.id, shared: e.target.checked })}
                          />
                          Widoczne dla podwykonawcy
                        </label>
                        <ErrorLine error={share.error} />
                      </OnlyUs>
                    )}
                  </div>
                )}
              </li>
            );
          })}
          {visible.length === 0 && <li className="card"><EmptyState>Nic nie pasuje do filtra</EmptyState></li>}
        </ul>
      )}
      <LetterDialog leadershipId={leadershipId} parcels={parcels} rows={rows} open={letterOpen} onClose={() => setLetterOpen(false)} />
    </div>
  );
}
